import { TaxReturnForm as SharedTaxReturnForm } from '@dec/shared';

import { useJurisdictions } from '@/app/modules/current-account/hooks/useJurisdictions';

import { type FormDefinitionResponse } from '../../models/definition.types';
import { type Period, type TaxReturnCell, type TaxReturnRequest } from '../../models/types';
import { useGetFormDefinition } from '../../services/formDefinition.service';
import { createTaxReturnNotificationsConnection } from '../../services/hubConnection';
import { useCreateConnection } from '../../services/signalr.hooks';
import { useFormLogRulesStore } from '../../stores/useFormLogRulesStore';
import {
  FormLoadingOverlay,
  FormProviders,
  FormRuntimeHandlers,
  strategyManager as Strategy
} from '../adapters/TaxReturnForm';

interface Props {
  formName?: string;
  version: string;
  taxpayerId?: string;
  amendedTaxReturnId?: string;
  readOnly?: boolean;
  period?: Period;
  defaultValue?: Array<TaxReturnCell>;
  submit: (data: TaxReturnRequest) => void;
}
export const TaxReturnForm = ({
  taxpayerId,
  version,
  period,
  defaultValue = [],
  amendedTaxReturnId,
  submit,
  readOnly = false,
  formName = 'TaxReturnForm'
}: Props) => {
  const { data: def, loading, error } = useGetFormDefinition(version);
  const jurisdictions = useJurisdictions();
  const { connection } = useCreateConnection(createTaxReturnNotificationsConnection, readOnly);
  const clearLogRules = useFormLogRulesStore((state) => state.clearLogRules);

  return (
    <SharedTaxReturnForm<TaxReturnRequest, FormDefinitionResponse, Period, TaxReturnCell>
      version={version}
      submit={submit}
      definition={def}
      loading={loading || jurisdictions.isLoading}
      error={error ?? jurisdictions.error}
      jurisdictions={jurisdictions.data}
      connection={connection}
      createComponent={Strategy.CreateComponent}
      createValidation={Strategy.CreateValidation}
      clearLogRules={clearLogRules}
      formName={formName}
      taxpayerId={taxpayerId}
      amendedTaxReturnId={amendedTaxReturnId}
      readOnly={readOnly}
      period={period}
      defaultValue={defaultValue}
      validationMode="onSubmit"
      wrapContent={({ children, loading }) => (
        <FormLoadingOverlay loading={loading}>{children}</FormLoadingOverlay>
      )}
      wrapProviders={({ children, runtime, connection }) => (
        <FormProviders runtime={runtime} connection={connection}>
          {children}
        </FormProviders>
      )}
      renderRuntime={({ readOnly, setLoading }) => (
        <FormRuntimeHandlers readOnly={readOnly} setLoading={setLoading} />
      )}
    />
  );
};

export default TaxReturnForm;
